/* ============================= */
/* PASTORAL EXECUTION ENGINE     */
/* Follow-through quality model  */
/* 100% deterministic — no AI    */
/* ============================= */

import { getDaysSince } from "./date";
import { TREND_TYPES } from "./pastoral-memory";

/* ============================= */
/* EXECUTION STATES              */
/* ============================= */

export const EXECUTION_STATE = {
  NOT_STARTED:       "NOT_STARTED",
  INITIATED:         "INITIATED",
  IN_PROGRESS:       "IN_PROGRESS",
  FOLLOWING_THROUGH: "FOLLOWING_THROUGH",
  SUSTAINED:         "SUSTAINED",
  FRAGMENTED:        "FRAGMENTED",
  RECOVERING:        "RECOVERING",
  STALLED:           "STALLED",
  OVERDUE:           "OVERDUE",
};

/* ============================= */
/* CADENCE QUALITY               */
/* Coefficient of Variation (CV) */
/* CV = stddev / mean            */
/* ============================= */

export const CADENCE_QUALITY = {
  NONE:       "NONE",
  INITIATED:  "INITIATED",
  CONSISTENT: "CONSISTENT",
  VARIABLE:   "VARIABLE",
  IRREGULAR:  "IRREGULAR",
};

/* ============================= */
/* STATIC LABEL MAPS             */
/* Pastoral voice — no corporate */
/* terms, no deadline language   */
/* ============================= */

const EXECUTION_LABELS = {
  NOT_STARTED:       "Sin acompañamiento iniciado",
  INITIATED:         "Acompañamiento iniciado",
  IN_PROGRESS:       "Seguimiento en curso",
  FOLLOWING_THROUGH: "Continuando el seguimiento",
  SUSTAINED:         null,
  FRAGMENTED:        "Ritmo irregular de visitas",
  RECOVERING:        "Retomando el seguimiento",
  STALLED:           "Seguimiento interrumpido",
  OVERDUE:           "Sin visita en mucho tiempo",
};

const EXECUTION_SUBLABELS = {
  NOT_STARTED:       null,
  INITIATED:         "Primeros contactos registrados",
  IN_PROGRESS:       "Visitas registradas en los últimos 45 días",
  FOLLOWING_THROUGH: "Visita reciente tras un período de ausencia",
  SUSTAINED:         null,
  FRAGMENTED:        "El ritmo de visitas no es constante",
  RECOVERING:        "Actividad retomada tras un período sin visitas",
  STALLED:           "Más de 3 meses sin visita registrada",
  OVERDUE:           "Requiere atención pastoral",
};

/* ============================= */
/* HELPERS                       */
/* ============================= */

function buildDaysAgoArr(visits) {
  return (visits || [])
    .filter((v) => !!v.date)
    .map((v) => getDaysSince(v.date))
    .filter((d) => d !== null && d >= 0)
    .sort((a, b) => a - b) // ascending: newest visit = smallest daysAgo
    .slice(0, 6);
}

function buildGaps(daysAgoArr) {
  const gaps = [];
  for (let i = 0; i < daysAgoArr.length - 1; i++) {
    const g = daysAgoArr[i + 1] - daysAgoArr[i];
    if (g > 0) gaps.push(g);
    if (gaps.length >= 5) break;
  }
  return gaps;
}

/* ============================= */
/* CADENCE COMPUTATION           */
/* CV < 0.3 → CONSISTENT        */
/* CV < 0.6 → VARIABLE          */
/* CV ≥ 0.6 → IRREGULAR         */
/* ============================= */

function computeCadenceQuality(gaps) {
  if (!gaps || gaps.length === 0) return CADENCE_QUALITY.NONE;
  if (gaps.length === 1)          return CADENCE_QUALITY.INITIATED;

  const mean = gaps.reduce((s, g) => s + g, 0) / gaps.length;
  if (mean === 0) return CADENCE_QUALITY.NONE;

  const variance = gaps.reduce((s, g) => s + (g - mean) ** 2, 0) / gaps.length;
  const cv = Math.sqrt(variance) / mean;

  if (cv < 0.3) return CADENCE_QUALITY.CONSISTENT;
  if (cv < 0.6) return CADENCE_QUALITY.VARIABLE;
  return CADENCE_QUALITY.IRREGULAR;
}

/* ============================= */
/* EXECUTION STATE DERIVATION    */
/* Priority-ordered — first      */
/* match wins                    */
/* ============================= */

function deriveExecutionState(visits, { currentDaysSince, momentum }) {
  const visitCount = (visits || []).filter((v) => !!v.date).length;
  const daysAgo    = currentDaysSince;
  const trend      = momentum?.trend     ?? TREND_TYPES.UNKNOWN;
  const visits90d  = momentum?.visits_90d ?? 0;

  const daysAgoArr  = buildDaysAgoArr(visits);
  const gaps        = buildGaps(daysAgoArr);
  const cadence     = computeCadenceQuality(gaps);
  const prevGapDays = daysAgoArr.length >= 2 ? daysAgoArr[1] - daysAgoArr[0] : null;

  // 1. NOT_STARTED — no visits, no history
  if (daysAgo === null && visitCount === 0) {
    return { state: EXECUTION_STATE.NOT_STARTED, cadence: CADENCE_QUALITY.NONE };
  }

  // 2. SUSTAINED — healthy trend + consistent recent rhythm
  if (trend === TREND_TYPES.STABLE_HEALTHY && visits90d >= 3 && cadence !== CADENCE_QUALITY.IRREGULAR) {
    return { state: EXECUTION_STATE.SUSTAINED, cadence };
  }

  // 3. OVERDUE — very long absence or no date available
  if (daysAgo === null || daysAgo > 120) {
    return { state: EXECUTION_STATE.OVERDUE, cadence };
  }

  // 4. STALLED — 90+ days, no visits in recent window
  if (daysAgo > 90 && visits90d === 0) {
    return { state: EXECUTION_STATE.STALLED, cadence };
  }

  // 5. RECOVERING — memory trend signals recovery
  if (trend === TREND_TYPES.RECOVERING) {
    return { state: EXECUTION_STATE.RECOVERING, cadence };
  }

  // 6. FOLLOWING_THROUGH — recent visit after notable absence
  if (daysAgo <= 14 && prevGapDays !== null && prevGapDays >= 45) {
    return { state: EXECUTION_STATE.FOLLOWING_THROUGH, cadence };
  }

  // 7. IN_PROGRESS — multiple visits within active window
  if (visits90d >= 2 && daysAgo <= 45) {
    return { state: EXECUTION_STATE.IN_PROGRESS, cadence };
  }

  // 8. FRAGMENTED — history exists but rhythm is inconsistent
  if (visitCount >= 3 && cadence === CADENCE_QUALITY.IRREGULAR) {
    return { state: EXECUTION_STATE.FRAGMENTED, cadence };
  }

  // 9. INITIATED — few visits, accompaniment just beginning
  if (visitCount <= 2) {
    return { state: EXECUTION_STATE.INITIATED, cadence };
  }

  // 10. FALLBACK
  return { state: EXECUTION_STATE.IN_PROGRESS, cadence };
}

/* ============================= */
/* ENTITY ENTRY POINT            */
/* ============================= */

/**
 * buildPastoralExecution — derive execution quality for a person or family.
 *
 * @param {Object[]} visits — full visit history, sorted date desc
 * @param {{ currentDaysSince?: number|null, momentum?: object }} options
 * @returns {{ state, cadence, label, sublabel }}
 */
export function buildPastoralExecution(visits, { currentDaysSince = null, momentum = null } = {}) {
  const { state, cadence } = deriveExecutionState(visits, { currentDaysSince, momentum });
  return {
    state,
    cadence,
    label:    EXECUTION_LABELS[state]    ?? null,
    sublabel: EXECUTION_SUBLABELS[state] ?? null,
  };
}

/* ============================= */
/* DISTRICT ENTRY POINT          */
/* Proxy — no N+1 fetches        */
/* Uses lastVisitDays from       */
/* church evaluations            */
/* ============================= */

/**
 * buildDistrictExecution — aggregate execution signal for district view.
 *
 * @param {Array<{ church, lastVisitDays, risk }>} churchEvaluations
 * @returns {{ summary: string|null, recentCount: number, stalledCount: number, overdueCount: number }}
 */
export function buildDistrictExecution(churchEvaluations) {
  if (!churchEvaluations || churchEvaluations.length === 0) {
    return { summary: null, recentCount: 0, stalledCount: 0, overdueCount: 0 };
  }

  let recentCount  = 0; // ≤ 45 days — active follow-through
  let stalledCount = 0; // 46–120 days — slowing down
  let overdueCount = 0; // > 120 days or null — needs attention

  for (const { lastVisitDays } of churchEvaluations) {
    if (lastVisitDays === null || lastVisitDays > 120) {
      overdueCount++;
    } else if (lastVisitDays > 45) {
      stalledCount++;
    } else {
      recentCount++;
    }
  }

  const parts = [];
  if (recentCount  > 0) parts.push(`${recentCount} con seguimiento reciente`);
  if (stalledCount > 0) parts.push(`${stalledCount} con visitas espaciadas`);
  if (overdueCount > 0) parts.push(`${overdueCount} sin visita reciente`);

  return {
    summary: parts.length > 0 ? parts.join(" · ") : null,
    recentCount,
    stalledCount,
    overdueCount,
  };
}
